import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import {
  Conditional,
  MyEquipmentCatIconSuccess,
  MyEquipmentSerialInput
} from 'components/pcc-components';
import { useEquipmentSearchSuggestions } from '../hooks/use-equipment-search-suggestions';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import {
  EQUIPMENT_SEARCH_FILTER,
  FORM_FIELD_SERIAL,
  GENERIC_ERROR,
  MEQ_SERIAL_NO_MATCH,
  SEARCH_LENGTH
} from '../constants';
import { validateSerialNumber } from '../add-equipment/add-equipment-form/add-equipment-by-serial/utils';
import { UseEquipmentSuggestionsResult } from '../types';
import { SelectedEquipmentInfoProps } from './types';
import { useFeedback } from 'providers/feedback-provider';
import { CatLoadingIndicator } from 'components/blocks-components';
import styles from './edit-equipment-drawer.module.scss';

export const EditEquipmentBySerial = ({
  selectedEquipmentInfo,
  setIsPendingSearchMachine
}: {
  selectedEquipmentInfo: SelectedEquipmentInfoProps;
  setIsPendingSearchMachine: Dispatch<SetStateAction<boolean>>;
}) => {
  const t = useTranslations();
  const { dismissFeedback, updateFeedback } = useFeedback();
  const textMEQSerialNoMatch = t('MEQ_SERIAL_NO_MATCH');
  const textValidMEQSerialModelMatch = t('MEQ_SERIAL_MODEL_MATCH');
  const [errorText, setErrorText] = useState('');
  const [isErrorEnabled, setIsErrorEnabled] = useState(!!errorText);
  const {
    trigger,
    getFieldState,
    getValues,
    clearErrors,
    watch,
    setError,
    formState: { errors }
  } = useFormContext();
  const watchBySerialField = watch(FORM_FIELD_SERIAL);

  const {
    error,
    suggestions,
    getEquipmentSuggestions,
    isFetching,
    clearSuggestions,
    genericError,
    setGenericError
  }: UseEquipmentSuggestionsResult = useEquipmentSearchSuggestions({
    isEditEquipment: true
  });
  const matchEquipment = suggestions?.equipments.find(
    (item) => item.model === selectedEquipmentInfo.model
  );
  const showSuccessIcon = !isEmpty(matchEquipment) && !isFetching;
  const validMEQSerialModelHaveNotMatch =
    !isEmpty(suggestions?.equipments) && isEmpty(matchEquipment) && !isFetching;
  const MEQSerialNoMatch =
    Array.isArray(suggestions?.equipments) && isEmpty(suggestions?.equipments);

  const handleSearch = (value: string): void => {
    if (value.length < 2) return;
    getEquipmentSuggestions({
      searchTerm: value,
      filter: EQUIPMENT_SEARCH_FILTER.SERIAL
    });
  };

  const clearFormErrors = useCallback(() => {
    !isEmpty(errors) && clearErrors();
    isErrorEnabled && setIsErrorEnabled(false);
    errorText !== '' && setErrorText('');
  }, [clearErrors, errorText, errors, isErrorEnabled]);

  const serialSearch = (str: string): void => {
    clearFormErrors();
    setGenericError(false);
    dismissFeedback();
    const validateSearch = async (str: string) => {
      await trigger(FORM_FIELD_SERIAL);
      if (!getFieldState(FORM_FIELD_SERIAL).error) {
        handleSearch(str);
      }
    };

    if (str !== getValues(FORM_FIELD_SERIAL)) {
      clearSuggestions();
      validateSearch(str);
    }
  };

  useEffect(() => {
    if (!genericError) return;
    updateFeedback(GENERIC_ERROR);
    clearSuggestions();
    clearFormErrors();
  }, [clearFormErrors, clearSuggestions, genericError, updateFeedback]);

  useEffect(() => {
    if (isEmpty(watchBySerialField)) {
      clearSuggestions();
      clearFormErrors();
    }
  }, [clearFormErrors, clearSuggestions, watchBySerialField]);

  useEffect(() => {
    if (MEQSerialNoMatch || error?.message === MEQ_SERIAL_NO_MATCH) {
      setErrorText(textMEQSerialNoMatch);
      setError(FORM_FIELD_SERIAL, {
        message: textMEQSerialNoMatch
      });
      setIsErrorEnabled(true);
    }

    if (validMEQSerialModelHaveNotMatch) {
      setErrorText(textValidMEQSerialModelMatch);
      setError(FORM_FIELD_SERIAL, {
        message: textValidMEQSerialModelMatch
      });
      setIsErrorEnabled(true);
    }
  }, [
    MEQSerialNoMatch,
    error,
    matchEquipment,
    setError,
    suggestions,
    textMEQSerialNoMatch,
    textValidMEQSerialModelMatch,
    validMEQSerialModelHaveNotMatch
  ]);

  useEffect(() => {
    trigger();
    setIsPendingSearchMachine(isFetching);
  }, [isFetching, setIsPendingSearchMachine, trigger]);

  return (
    <div className="position-relative">
      <MyEquipmentSerialInput
        placeholder={t('ORDER_HISTORY_SERIALNUM_PLACEHOLDER')}
        name={FORM_FIELD_SERIAL}
        maxLength={SEARCH_LENGTH.MAX}
        onChange={serialSearch}
        fieldNote={errorText || null}
        isError={isErrorEnabled}
        rules={{
          validate: {
            validateSerialNumber: (fieldValue) => {
              if (error?.message) return false ;
              setIsErrorEnabled(false);
              if (isEmpty(fieldValue)) return true;
              if (validateSerialNumber(fieldValue).isValid) return true;

              setIsErrorEnabled(true);
              return t('MEQ_SERIAL_INVALID');
            }
          }
        }}
        dataCsMask
      />
      <MyEquipmentCatIconSuccess showIcon={showSuccessIcon} />
      <Conditional test={isFetching && isEmpty(errorText)}>
        <CatLoadingIndicator
          slot="after"
          className={`${styles['inline-loader-container']}`}
        />
      </Conditional>
    </div>
  );
};
